<template>
  <div
    class="upload-field"
    :class="{
      '-disabled': fieldConfig && fieldConfig.disabled,
      '-error': !!errorMessage,
    }"
  >
    <FieldLabel
      :id="fieldConfig && fieldConfig.id"
      :label="fieldConfig && fieldConfig.label"
      :description="fieldConfig && fieldConfig.description"
      :required="fieldConfig && fieldConfig.required"
    />

    <!-- Hidden native input; triggered by the browse button below -->
    <input
      :id="fieldConfig && fieldConfig.id"
      ref="input"
      type="file"
      class="visually-hidden-input"
      :accept="normalizedAccept"
      :disabled="fieldConfig && fieldConfig.disabled"
      :required="fieldConfig && fieldConfig.required"
      :aria-invalid="errorMessage ? 'true' : 'false'"
      :multiple="isMultiple"
      @change="onFileChange"
    />

    <div
      class="dropzone"
      :class="{ '-dragover': isDragOver }"
      role="button"
      tabindex="0"
      aria-label="Datei hierhin ziehen oder klicken, um auszuwählen"
      @keydown.enter.prevent="openFileDialog"
      @keydown.space.prevent="openFileDialog"
      @dragenter="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <div class="dropzone-instructions">
        <div class="headline">{{ dropLabel }}</div>

        <div
          v-if="fileTypeLabels && fileTypeLabels.length"
          class="file-type-card-stack"
          aria-hidden="true"
        >
          <div v-for="ext in fileTypeLabels" :key="ext" class="card">
            <svg
              class="card-bg"
              width="84"
              height="100"
              viewBox="0 0 87 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              preserveAspectRatio="xMidYMid meet"
            >
              <g transform="translate(-10,-10) scale(1.35)">
                <path
                  d="M20.4337 66.5937L19.5524 16.1013C19.5234 14.4447 20.8429 13.0784 22.4995 13.0494L49.2528 12.5825C50.0484 12.5686 50.815 12.8695 51.3873 13.4222L66.7721 28.2791C67.9083 29.3763 67.7783 52.2426 67.5347 65.8918C67.5059 67.5036 66.2043 68.7952 64.5925 68.8233L23.4856 69.5408C21.829 69.5698 20.4626 68.2503 20.4337 66.5937Z"
                  fill="url(#upl_paint0)"
                />
                <path
                  d="M50.4953 12.5608L66.7721 28.2791L54.7739 28.4885C52.5651 28.5271 50.7433 26.7678 50.7047 24.559L50.4953 12.5608Z"
                  fill="#EDEDED"
                />
              </g>
              <defs>
                <filter
                  id="upl_filter0_d"
                  x="0.651892"
                  y="0.682001"
                  width="85.9482"
                  height="94.7593"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="7" />
                  <feGaussianBlur stdDeviation="9.45" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="upl_paint0"
                  x1="43.3277"
                  y1="12.6859"
                  x2="44.3137"
                  y2="69.1773"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#F7FfFf" />
                  <stop offset="1" stop-color="#ECF0F3" />
                </linearGradient>
              </defs>
            </svg>
            <span class="card-text">{{ ext }}</span>
          </div>
        </div>
      </div>

      <label :for="fieldConfig && fieldConfig.id" class="browse-button">
        <span class="paperclip" aria-hidden="true">📎</span>
        <span class="browse-text">{{ browseLabel }}</span>
      </label>
    </div>

    <label
      v-if="errorMessage"
      :for="fieldConfig && fieldConfig.id"
      class="field-error"
    >
      {{ errorMessage }}
    </label>
    <div v-if="!isMultiple && fileName" class="file-row">
      <span class="file-name">{{ fileName }}</span>
      <button type="button" class="clear-button" @click="handleClear">
        {{ clearButtonLabel }}
      </button>
    </div>
    <div v-if="isMultiple && fileList.length" class="file-list">
      <div v-for="(file, index) in fileList" :key="index" class="file-row">
        <span class="file-name">{{ file.name }}</span>
        <button
          type="button"
          class="clear-button"
          @click="handleRemoveFile(index)"
        >
          {{ clearButtonLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FieldLabel from "../FieldLabel/FieldLabel.vue";
import errorDisplay from "../mixins/errorDisplay.js";
import nativeChangeGuard from "../mixins/nativeChangeGuard.js";
import vModelHelpers from "../mixins/vModelHelpers.js";

export default {
  name: "UploadField",
  components: {
    FieldLabel,
  },
  mixins: [errorDisplay, nativeChangeGuard, vModelHelpers],
  props: {
    fieldConfig: {
      type: Object,
      default: () => ({
        id: "upload-field",
        name: "",
        type: "upload",
        label: "",
        placeholder: "",
        required: false,
        width: "FULL",
        disabled: false,
        maxFileSize: undefined,
        maxFileCount: undefined,
        maxTotalSize: undefined,
        allowedExtensions: undefined,
        uploadUrl: undefined,
        dropLabel: "Ziehen und Ablegen von Dateien zum Hochladen",
        browseLabel: "Klicken Sie zum Auswählen von Dateien",
        clearLabel: "× Remove file",
        messages: {
          fileTooLarge: "File is too large",
          fileTypeNotAllowed: "File type is not allowed",
          tooManyFiles: "Too many files selected",
          totalSizeTooLarge: "Total file size is too large",
        },
      }),
    },
    // For symmetry with other fields. Value is the File(s) or a string name.
    value: {
      type: [Object, String, Array],
      default: null,
    },
    modelValue: {
      type: [Object, String, Array],
      default: undefined,
    },
  },
  emits: ["input", "update:modelValue", "fileSelected", "fileCleared"],
  data() {
    return {
      internalValue: this.initialFromModel(this.modelValue, this.value, null),
      internalError: "",
      fileName: "",
      fileList: [],
      isDragOver: false,
    };
  },
  computed: {
    isMultiple() {
      const maxFileCount = this.fieldConfig && this.fieldConfig.maxFileCount;
      return typeof maxFileCount === "number" && maxFileCount > 1;
    },
    // Override mixin's errorMessage to surface local validation errors first
    errorMessage() {
      return this.internalError || this.error;
    },
    dropLabel() {
      return (
        (this.fieldConfig && this.fieldConfig.dropLabel) ||
        "Ziehen und Ablegen von Dateien zum Hochladen"
      );
    },
    browseLabel() {
      return (
        (this.fieldConfig && this.fieldConfig.browseLabel) ||
        "Klicken Sie zum Auswählen von Dateien"
      );
    },
    normalizedAccept() {
      const cfg = this.fieldConfig;
      const list = cfg && cfg.allowedExtensions ? cfg.allowedExtensions : [];
      if (!list || !list.length) return "";
      const parts = [];
      for (let i = 0; i < list.length; i++) {
        const ext = list[i];
        if (!ext) continue;
        const low = String(ext).toLowerCase();
        const val = low[0] === "." ? low : "." + low;
        if (parts.indexOf(val) === -1) parts.push(val);
      }
      return parts.join(",");
    },
    fileTypeLabels() {
      const labels = [];
      const cfg = this.fieldConfig;
      const allowed = cfg && cfg.allowedExtensions;
      if (allowed && allowed.length) {
        for (let i = 0; i < allowed.length; i++) {
          const raw = allowed[i];
          if (!raw) continue;
          const ext = String(raw).toLowerCase();
          const withDot = ext[0] === "." ? ext : "." + ext;
          if (labels.indexOf(withDot) === -1) labels.push(withDot);
        }
      } else {
        const acc = cfg && cfg.accept;
        if (acc && typeof acc === "string") {
          const parts = acc.split(",");
          for (let j = 0; j < parts.length; j++) {
            const part = parts[j] && parts[j].trim();
            if (!part) continue;
            if (part.indexOf("/") !== -1) continue;
            const norm =
              part[0] === "." ? part.toLowerCase() : "." + part.toLowerCase();
            if (labels.indexOf(norm) === -1) labels.push(norm);
          }
        }
      }
      return labels;
    },
    clearButtonLabel() {
      const config = this.fieldConfig;
      const label = config && config.clearLabel;
      return label || "× Remove file";
    },
  },
  watch: {
    value(newVal) {
      if (this.modelValue !== undefined) return;
      this.internalValue = newVal;
      this.updateFileDisplay(newVal);
    },
    modelValue(newVal) {
      this.internalValue = newVal;
      this.updateFileDisplay(newVal);
    },
  },
  methods: {
    setDragOver(e, value) {
      if (this.fieldConfig && this.fieldConfig.disabled) return;
      if (e && e.preventDefault) e.preventDefault();
      if (e && e.stopPropagation) e.stopPropagation();
      this.isDragOver = !!value;
    },
    openFileDialog() {
      if (this.fieldConfig && this.fieldConfig.disabled) return;
      if (this.$refs && this.$refs.input && this.$refs.input.click) {
        this.$refs.input.click();
      }
    },
    updateFileDisplay(value) {
      if (this.isMultiple) {
        if (Array.isArray(value)) {
          this.fileList = value;
        } else {
          this.fileList = [];
        }
      } else {
        if (value && value.name) {
          this.fileName = value.name;
        } else if (typeof value === "string") {
          this.fileName = value;
        } else {
          this.fileName = "";
        }
      }
    },
    onDragEnter(e) {
      this.setDragOver(e, true);
    },
    onDragOver(e) {
      this.setDragOver(e, true);
    },
    onDragLeave(e) {
      this.setDragOver(e, false);
    },
    onDrop(event) {
      if (this.fieldConfig && this.fieldConfig.disabled) return;
      this.setDragOver(event, false);

      const dataTransfer =
        event && event.dataTransfer ? event.dataTransfer : null;
      const droppedFiles =
        dataTransfer && dataTransfer.files && dataTransfer.files.length
          ? dataTransfer.files
          : null;

      if (this.isMultiple) {
        this.handleMultipleFiles(droppedFiles);
      } else {
        const file = droppedFiles && droppedFiles[0] ? droppedFiles[0] : null;
        this.handleFile(file);
      }

      // Sync the native input with dropped files
      this.syncNativeInput(droppedFiles);
    },
    onFileChange(event) {
      this.stopNativeChange(event);
      const selectedFiles =
        event && event.target && event.target.files ? event.target.files : null;

      if (this.isMultiple) {
        this.handleMultipleFiles(selectedFiles);
      } else {
        const file =
          selectedFiles && selectedFiles.length > 0 ? selectedFiles[0] : null;
        this.handleFile(file);
      }
    },
    handleFile(file) {
      this.internalError = "";
      if (file) {
        const maxSize = this.fieldConfig && this.fieldConfig.maxFileSize;
        if (typeof maxSize === "number" && file.size > maxSize) {
          const messages = this.fieldConfig && this.fieldConfig.messages;
          this.internalError =
            (messages && messages.fileTooLarge) || "File is too large";
        }

        const maxTotalSize = this.fieldConfig && this.fieldConfig.maxTotalSize;
        if (
          !this.internalError &&
          typeof maxTotalSize === "number" &&
          file.size > maxTotalSize
        ) {
          const messages = this.fieldConfig && this.fieldConfig.messages;
          this.internalError =
            (messages && messages.fileTooLarge) || "File is too large";
        }

        const allowed = this.fieldConfig && this.fieldConfig.allowedExtensions;
        if (!this.internalError && allowed && allowed.length > 0) {
          const fileExtension = this.extractExtension(file.name);
          const isAllowed = this.isAllowedExtension(fileExtension, allowed);
          if (!isAllowed) {
            const messages = this.fieldConfig && this.fieldConfig.messages;
            this.internalError =
              (messages && messages.fileTypeNotAllowed) ||
              "File type is not allowed";
          }
        }
      } else if (this.fieldConfig && this.fieldConfig.required) {
        const config = this.fieldConfig;
        const message =
          (config && config.messages && config.messages.required) ||
          "This field is required";
        this.internalError = message;
      }

      if (this.internalError) {
        this.internalValue = null;
        this.fileName = "";
        this.emitModel(null);
        this.$emit("fileCleared", {
          name: this.fieldConfig && this.fieldConfig.name,
          field: this.fieldConfig,
        });
        return;
      }

      this.internalValue = file;
      this.updateFileDisplay(file);
      this.emitModel(file);
      this.$emit("fileSelected", {
        file: file,
        name: this.fieldConfig && this.fieldConfig.name,
        field: this.fieldConfig,
      });
    },
    handleMultipleFiles(fileList) {
      this.internalError = "";

      if (!fileList || fileList.length === 0) {
        // User canceled selection, keep existing files
        return;
      }

      const newFiles = Array.from(fileList);
      const existingFiles = Array.isArray(this.fileList) ? this.fileList : [];
      const combinedFiles = [...existingFiles, ...newFiles];
      const maxFileCount = this.fieldConfig && this.fieldConfig.maxFileCount;

      if (
        typeof maxFileCount === "number" &&
        combinedFiles.length > maxFileCount
      ) {
        const messages = this.fieldConfig && this.fieldConfig.messages;
        this.internalError =
          (messages && messages.tooManyFiles) || "Too many files selected";
        return;
      }

      const maxSize = this.fieldConfig && this.fieldConfig.maxFileSize;
      const maxTotalSize = this.fieldConfig && this.fieldConfig.maxTotalSize;
      const allowed = this.fieldConfig && this.fieldConfig.allowedExtensions;

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];

        if (typeof maxSize === "number" && file.size > maxSize) {
          const messages = this.fieldConfig && this.fieldConfig.messages;
          this.internalError =
            (messages && messages.fileTooLarge) || "File is too large";
          return;
        }

        if (allowed && allowed.length > 0) {
          const fileExtension = this.extractExtension(file.name);
          const isAllowed = this.isAllowedExtension(fileExtension, allowed);
          if (!isAllowed) {
            const messages = this.fieldConfig && this.fieldConfig.messages;
            this.internalError =
              (messages && messages.fileTypeNotAllowed) ||
              "File type is not allowed";
            return;
          }
        }
      }

      if (typeof maxTotalSize === "number") {
        const totalSize = combinedFiles.reduce(
          (sum, file) => sum + (file.size || 0),
          0,
        );
        if (totalSize > maxTotalSize) {
          const messages = this.fieldConfig && this.fieldConfig.messages;
          this.internalError =
            (messages && messages.totalSizeTooLarge) ||
            "Total file size is too large";
          return;
        }
      }

      this.internalValue = combinedFiles;
      this.fileList = combinedFiles;
      this.emitModel(combinedFiles);
      this.$emit("fileSelected", {
        file: combinedFiles,
        name: this.fieldConfig && this.fieldConfig.name,
        field: this.fieldConfig,
      });
    },
    handleRemoveFile(index) {
      const newFileList = [...this.fileList];
      newFileList.splice(index, 1);

      this.internalError = "";
      this.internalValue = newFileList;
      this.fileList = newFileList;
      this.emitModel(newFileList);

      // Sync native input with updated file list
      this.syncNativeInput(newFileList);

      if (newFileList.length === 0) {
        this.$emit("fileCleared", {
          name: this.fieldConfig && this.fieldConfig.name,
          field: this.fieldConfig,
        });
      } else {
        this.$emit("fileSelected", {
          file: newFileList,
          name: this.fieldConfig && this.fieldConfig.name,
          field: this.fieldConfig,
        });
      }
    },
    handleClear() {
      this.internalError = "";

      if (this.isMultiple) {
        this.internalValue = [];
        this.fileList = [];
        this.emitModel([]);
      } else {
        this.internalValue = null;
        this.fileName = "";
        this.emitModel(null);
      }

      // Clear the native input
      this.syncNativeInput(null);

      this.$emit("fileCleared", {
        name: this.fieldConfig && this.fieldConfig.name,
        field: this.fieldConfig,
      });
    },
    extractExtension(fileName) {
      const lastDotIndex = fileName && fileName.lastIndexOf(".");
      if (
        lastDotIndex === -1 ||
        lastDotIndex === undefined ||
        lastDotIndex === null
      )
        return "";
      return fileName.substring(lastDotIndex + 1).toLowerCase();
    },
    isAllowedExtension(extension, allowedExtensions) {
      if (!extension || !allowedExtensions) return true;
      for (let i = 0; i < allowedExtensions.length; i++) {
        const allowedItem = allowedExtensions[i];
        if (
          allowedItem &&
          allowedItem.toLowerCase &&
          allowedItem.toLowerCase() === extension
        )
          return true;
      }
      return false;
    },
    syncNativeInput(fileList) {
      // Sync the native input element with the dropped files
      // This ensures the native required validation works correctly in production
      if (!this.$refs.input) return;

      try {
        if (!fileList || fileList.length === 0) {
          // Clear the native input
          this.$refs.input.value = "";
          return;
        }

        const dataTransfer = new DataTransfer();
        const filesArray = Array.from(fileList);

        for (let i = 0; i < filesArray.length; i++) {
          dataTransfer.items.add(filesArray[i]);
        }

        this.$refs.input.files = dataTransfer.files;
      } catch (error) {
        // DataTransfer API might not be available in some browsers
        console.warn("Could not sync native input:", error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/abstracts/mixins";
@import "../styles/field-shared";

.upload-field {
  width: 100%;
}

.visually-hidden-input {
  position: absolute;

  overflow: hidden;

  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;

  white-space: nowrap;

  clip: rect(0, 0, 0, 0);
  border: 0;
}

.dropzone {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;

  padding: 20px;

  background: var(--cr-form-background, rgb(207 232 255 / 15%));
  border: 1px dashed var(--cr-form-field-border-color, #e5e7eb);
  border-radius: 8px;

  &.-dragover {
    background-color: color-mix(
      in srgb,
      var(--cr-form-background, #fff) 92%,
      #000 8%
    );
  }
}

.dropzone-instructions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.headline {
  font-weight: 600;
  color: var(--cr-form-label-color, inherit);
  text-align: center;

  @include font-family("body");
  @include scale-fonts("form-label");
}

.file-type-card-stack {
  display: flex;
  gap: 0;
}

.file-type-card-stack .card {
  position: relative;
  z-index: 1;
  transform: rotate(var(--rot));

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 84px;
  height: 100px;

  transition: transform 0.15s ease;

  --rot: 0deg;
}

.file-type-card-stack .card-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.file-type-card-stack .card-text {
  position: relative;
  z-index: 1;

  font-size: 13px;
  font-weight: 600;
  color: #1d1d1f;

  @include font-family("body");
}

.file-type-card-stack .card + .card {
  margin-left: -18px;
}

.file-type-card-stack .card:nth-child(2) {
  z-index: 2;
}

.file-type-card-stack .card:nth-child(3) {
  z-index: 3;
}

/* sheets rotate */
.file-type-card-stack .card:first-child {
  --rot: -2deg;
}

.file-type-card-stack .card:last-child {
  --rot: 4deg;
}

.file-type-card-stack .card:only-child {
  --rot: 0deg;
}

.browse-button {
  cursor: pointer;
  user-select: none;

  display: inline-flex;
  gap: 8px;
  align-items: center;

  padding: 10px 16px;

  color: var(--cr-form-label-color, inherit);
  text-decoration: none;

  background: none;
  border: 2px solid var(--cr-form-label-color, currentColor);
  border-radius: 9999px;

  @include font-family("body");
  @include scale-fonts("form-label");
}

.paperclip {
  line-height: 1;
}

.field-input::file-selector-button {
  color: var(--cr-form-field-text-color, inherit);

  @include scale-fonts("form-label");
}

.file-name {
  margin-top: 6px;
  color: var(--cr-form-field-text-color, inherit);

  @include font-family("body");
  @include scale-fonts("form-label");
}

.file-row {
  display: flex;
  gap: 8px;
  align-items: baseline;
  margin-top: 6px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.clear-button {
  cursor: pointer;

  margin-left: 10px;
  padding: 0;

  color: var(--cr-form-label-color);

  appearance: none;
  background: none;
  border: none;

  @include font-family("body");
  @include scale-fonts("form-label");
}

.-disabled {
  opacity: 0.6;

  .browse-button {
    cursor: not-allowed;
  }
}

/* Error state: highlight dropzone border with error color */
.-error .dropzone {
  border-color: var(--cr-form-error-color, #d00);
}
</style>
